import React, { useEffect, useRef, useState } from 'react';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';

import { TextInputArea } from '@uhg-abyss/web/ui/TextInputArea';
import { FileUpload } from '@uhg-abyss/web/ui/FileUpload';
import { styled } from '@uhg-abyss/web/tools/styled';
import { DateInput } from '@uhg-abyss/web/ui/DateInput';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { RadioGroup } from '@uhg-abyss/web/ui/RadioGroup';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import PropTypes from 'prop-types';
import { LagoonContext } from '@src/AppContext';
import { useDownloadFile } from '@src/hooks/useDownloadFile/useDownloadFile';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
const Wrapper = styled('div', {
  '.abyss-descriptors-error-message': {
    fontSize: '13px ',
    fontWeight: '400',
  },
});

// The vendor FileUpload widget accepts a `maxFiles` prop but never enforces it
// internally, so the cap has to be validated in app code instead.
const MAX_FILES = 2;

export const CreateCommunicationFormFields = ({
  setDraftFiles,
  downloadedFiles,
  draftFiles,
}) => {
  const form = useForm();
  const isFilesAvailable = form.getValues('isFilesAvailable');
  const [downloadFile] = useDownloadFile();
  const fileUploadWrapperRef = useRef(null);
  const lagoonContext = React.useContext(LagoonContext);
  const commCornerAudience = lagoonContext?.getTable(
    'communication-corner-audience'
  );

  const [apiOptions, setApiOptions] = useState([]);
  const [selectedAudience, setSelectedAudience] = useState([]);

  const symbol = () => {
    return <span style={{ color: '#C40000', fontSize: '23px' }}>*</span>;
  };

  const currentDate = () => {
    return dayjs();
  };

  const nextTwoMonths = () => {
    return dayjs().add(2, 'month');
  };

  const fileTypesAccepted = {
    'application/pdf': ['.pdf'],
  };

  const textInputAreaCss = {
    'abyss-text-input-area-label': {
      position: 'relative',
      fontSize: '15px',
    },
    'abyss-text-input-area': {
      height: '48px',
    },
  };

  const headerContent = (
    <React.Fragment>
      <p style={{ fontSize: '15px !important' }}>
        <b> Accepted File Type</b>
      </p>
      <p
        css={{
          'abyss-file-upload-header-description-text': {
            fontSize: '15px !important',
          },
        }}
      >
        PDF (Portable Document Format)
      </p>
      <div
        css={{
          'abyss-file-upload-header-heading-text': {
            marginTop: '15px',
            fontSize: '15px !important',
          },
        }}
      >
        <b>Please do not include any PHI/PII information on the attachments</b>
      </div>
    </React.Fragment>
  );

  useEffect(() => {
    const activeApis = commCornerAudience?.getRow()?.filter((row) => {
      return row?.active;
    });
    const apis = activeApis?.map((row) => {
      return { label: row?.label, value: row?.value, isDisabled: false };
    });
    setApiOptions(apis ?? []);
  }, [apiOptions.length]);

  useEffect(() => {
    form.setValue('audience', selectedAudience);
  }, [selectedAudience]);

  const isAllUsersOption = (option) => {
    return (
      option === 'All Users' || option?.toLowerCase().includes('all users')
    );
  };

  const handleAudienceChange = (newValue) => {
    const allUsersOption = newValue?.find(isAllUsersOption);

    if (allUsersOption) {
      setSelectedAudience([allUsersOption]);
      setApiOptions((prevOptions) => {
        return prevOptions.map((option) => {
          return {
            ...option,
            isDisabled: option.label !== 'All Users',
          };
        });
      });
    } else {
      setSelectedAudience(newValue);
      setApiOptions((prevOptions) => {
        return prevOptions.map((option) => {
          return { ...option, isDisabled: false };
        });
      });
    }
  };

  const validateFile = (file) => {
    const specCharRegex = /[!$%^&*()_+|~=`{}[\]:/;<>?,@#]/;
    if (file.name && specCharRegex.test(file.name)) {
      return 'File name cannot contain special characters other than hyphens.';
    }
    // draftFiles reflects the widget's own already-accepted files (not yet
    // updated for the file currently being validated), so these checks can't
    // flag the initial draft-attachment auto-load against itself.
    const attachedFileCount =
      draftFiles?.filter((queuedFile) => {
        return queuedFile?.status !== 'error';
      }).length ?? 0;
    if (attachedFileCount >= MAX_FILES) {
      return `${file.name} - Number of total files exceeded.`;
    }
    const isDuplicate = draftFiles?.some((queuedFile) => {
      return queuedFile?.file?.name === file?.name;
    });
    if (isDuplicate) {
      return `${file.name} - File has already been added.`;
    }
    return null;
  };

  // FileUpload keeps its own internal file-list state and only updates it from
  // real drop/select events, so pre-existing (downloaded) files must be fed in
  // through its actual <input type="file"> rather than via model/value props.
  useEffect(() => {
    if (!downloadedFiles?.length) {
      return;
    }
    const inputEl =
      fileUploadWrapperRef.current?.querySelector('input[type="file"]');
    if (!inputEl) {
      return;
    }
    const dataTransfer = new DataTransfer();
    downloadedFiles.forEach((file) => {
      if (file instanceof File) {
        dataTransfer.items.add(file);
      }
    });
    if (dataTransfer.files.length) {
      inputEl.files = dataTransfer.files;
      inputEl.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }, [downloadedFiles]);

  return (
    <Wrapper>
      <Layout.Stack
        space={40}
        css={{
          'abyss-layout-stack': {
            display: 'block',
            marginTop: '-2px',
            marginBottom: '20px',
          },
        }}
      >
        <div style={{ fontSize: '15px' }}>Required {symbol()}</div>

        <SelectInput
          id="audience"
          label="Audience"
          model="audience"
          width="330px"
          placeholder=""
          isClearable
          isSearchable
          isMulti
          options={apiOptions}
          onChange={handleAudienceChange}
          value={selectedAudience}
          validators={{ required: true }}
          css={{
            'abyss-select-input-label': {
              position: 'relative',
              fontSize: '15px',
            },
          }}
        />
        <Flex justify="space-between">
          <div>
            <DateInput
              id="date"
              label="Release Date"
              model="date"
              width="280px"
              minDate={currentDate()}
              maxDate={nextTwoMonths()}
              validators={{ required: true }}
              css={{
                'abyss-date-picker-input-label': {
                  position: 'relative',
                  fontSize: '15px',
                },
              }}
            />
          </div>
          <div>
            <TextInput
              id="subject"
              label="Subject/RE:"
              type="text"
              model="subject"
              width="330px"
              isClearable
              validators={{ required: true }}
              css={{
                'abyss-text-input-label': {
                  position: 'relative',
                  fontSize: '15px',
                },
              }}
            />
          </div>
        </Flex>
        <TextInputArea
          id="topic"
          label="Topic"
          model="topic"
          validators={{ required: true }}
          css={textInputAreaCss}
        />
        <TextInputArea
          id="actionByUnitedHealthCare"
          label="Action by UnitedHealthcare"
          model="actionByUnitedHealthCare"
          validators={{ required: true }}
          css={textInputAreaCss}
        />
        <TextInputArea
          id="clientActions"
          label="Client Actions"
          model="clientActions"
          validators={{ required: true }}
          css={textInputAreaCss}
        />
        <RadioGroup
          label="Do you need to attach any files?"
          model="isFilesAvailable"
          validators={{ required: true }}
          css={{
            'abyss-radio-group-label': {
              position: 'relative',
              fontSize: '15px',
            },
          }}
        >
          <RadioGroup.Column>
            <RadioGroup.Radio label="Yes" value="yes" />
          </RadioGroup.Column>
          <RadioGroup.Column>
            <RadioGroup.Radio label="No" value="no" />
          </RadioGroup.Column>
        </RadioGroup>
        {isFilesAvailable === 'yes' && (
          <div ref={fileUploadWrapperRef}>
            <FileUpload
              label="Subtext"
              subText={headerContent}
              fileTypes={fileTypesAccepted}
              css={{
                'abyss-file-upload-wrapper': {
                  padding: '18px',
                  borderRadius: '4px',
                  border: '1px solid #4B4D4F',
                },
                'abyss-file-upload-body-container': {
                  paddingTop: '20px',
                  paddingBottom: '20px',
                  fontSize: '15px',
                },
                'abyss-file-upload-label': {
                  position: 'absolute',
                  fontSize: '15px',
                },
                'abyss-file-upload-error-list-container': {
                  fontSize: '15px',
                },
              }}
              model="fileUpload"
              onChange={setDraftFiles}
              maxFileSize={2.5}
              maxFiles={MAX_FILES}
              validators={{
                required:
                  'You have chosen Yes for attachment, please attach a valid PDF file.',
              }}
              customValidation={validateFile}
              isUploading={downloadFile?.loading}
            />
          </div>
        )}
      </Layout.Stack>
    </Wrapper>
  );
};

CreateCommunicationFormFields.propTypes = {
  setDraftFiles: PropTypes.func.isRequired,
  downloadedFiles: PropTypes.arrayOf(PropTypes.instanceOf(File)),
  draftFiles: PropTypes.arrayOf(PropTypes.object),
};

CreateCommunicationFormFields.defaultProps = {
  downloadedFiles: [],
  draftFiles: [],
};
